import React, { PropsWithChildren, useMemo } from 'react';
import { ConfigurationRuleSetupType } from '@ydistri/api-sdk';
import { Popover } from 'antd';
import { Column, Text } from '@ydistri/ds';
import { useStoresReport } from './useStoresReport';
import { overriddenFields } from './storesReportLib';
import { useGetConfigurationRulesQuery } from './apiScopes';
import { useTemplateOrCalculationWithValue } from '../../../hooks/useTemplateOrCalculationWithValue';
import { generateScopeTitle } from './scopeUtilsLib';

interface ScopeConfigurationFieldPopoverProps extends PropsWithChildren {
  scopeId: number;
  field: ConfigurationRuleSetupType;
}

/** Names the later exceptions the server says override this field on an overlapping store. */
const ScopeConfigurationFieldPopover: React.FC<ScopeConfigurationFieldPopoverProps> = ({
  scopeId,
  field,
  children,
}) => {
  const templateOrCalculation = useTemplateOrCalculationWithValue();
  const { problems } = useStoresReport();
  const { data: scopes } = useGetConfigurationRulesQuery(templateOrCalculation.value);

  const overridingNames = useMemo(() => {
    const ids = overriddenFields(problems, scopeId)[field] ?? [];
    return ids.map(id => {
      const scope = scopes?.find(s => s.id === id);
      if (!scope) return `Exception ${id}`;
      return scope.name && scope.name !== '' ? scope.name : generateScopeTitle(scope);
    });
  }, [field, problems, scopeId, scopes]);

  const popoverContent = useMemo(
    () => (
      <Column>
        <Text>This setting is partially overwritten in these exceptions:</Text>
        <ul>
          {overridingNames.map(name => (
            <li key={name}>{name}</li>
          ))}
        </ul>
      </Column>
    ),
    [overridingNames],
  );

  if (overridingNames.length === 0) {
    return children;
  }
  return (
    <Popover content={popoverContent}>
      <div>{children}</div>
    </Popover>
  );
};

export default ScopeConfigurationFieldPopover;
